import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

const linkCls = ({ isActive }) =>
  `font-tech text-xs uppercase tracking-[0.2em] transition-colors hover:text-white ${isActive ? "text-[#FF3333]" : "text-zinc-400"}`;

const NavLinks = ({ user, onNavigate }) => (
  <>
    <a href="/#spaces" onClick={onNavigate} data-testid="nav-spaces-link" className={linkCls({})}>Spaces</a>
    <a href="/#hours" onClick={onNavigate} data-testid="nav-hours-link" className={linkCls({})}>Hours</a>
    {user && (
      <NavLink to="/my-bookings" onClick={onNavigate} data-testid="nav-my-bookings-link" className={linkCls}>My Bookings</NavLink>
    )}
    {user?.role === "admin" && (
      <NavLink to="/admin" onClick={onNavigate} data-testid="nav-admin-link" className={linkCls}>Admin</NavLink>
    )}
  </>
);

const AuthButtons = ({ user, onLogout, onNavigate }) =>
  user ? (
    <div className="flex items-center gap-4">
      <span className="hidden font-tech text-xs text-zinc-500 lg:inline" data-testid="nav-user-name">{user.name}</span>
      <button onClick={onLogout} data-testid="nav-logout-btn" className="text-zinc-400 transition-colors hover:text-[#FF3333]" aria-label="Log out">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  ) : (
    <Link to="/login" onClick={onNavigate} data-testid="nav-login-link" className={linkCls({})}>Log in</Link>
  );

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const handleLogout = async () => {
    await logout();
    close();
    toast.success("Logged out");
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          <NavLinks user={user} />
          <AuthButtons user={user} onLogout={handleLogout} />
          <Link to="/book" data-testid="nav-book-now" className="btn-riot !px-5 !py-2 text-xs">Book Studio</Link>
        </nav>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="text-white md:hidden" data-testid="nav-mobile-menu-btn" aria-label="Open menu">
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="flex flex-col gap-6 border-white/10 bg-[#0A0A0C] pt-16" data-testid="nav-mobile-menu">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <NavLinks user={user} onNavigate={close} />
            <AuthButtons user={user} onLogout={handleLogout} onNavigate={close} />
            <Link to="/book" onClick={close} data-testid="nav-mobile-book-now" className="btn-riot">Book Studio</Link>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
